import { useEffect } from 'react';
import useGameStore, { floatSharesOf, getDistrictTotal, rentYieldOf } from '../store/gameStore';
import type { PropertyTile } from '../types/gameTypes';
import { money } from '../lib/format';
import { DISTRICT_META } from './districtMeta';
import { DistrictIcon } from './GameIcons';
import { MAX_DISTRICT_TILES } from '../types/gameTypes';
import { tileName as localizeTile, useT } from '../i18n';

/**
 * New Property Spotlight — announces every freshly zoned annex (Urban Expansion)
 * with full details: district badge, initial valuation, rent & yield, public float
 * and the purchase rules. [Prohlédnout na desce] dismisses the card and starts a
 * 5-second glowing ring on the board so the player can immediately find the tile.
 *
 * When a BOT is playing the card auto-dismisses (it only ever blocks humans) —
 * the board glow and the ticker line still announce the annex.
 */
const NewPropertyAlertModal = () => {
  const store = useGameStore();
  const t = useT();
  const tile = store.newPropertyAlerts[0] ?? null;
  const active = store.players[store.currentPlayerIndex];

  // Bot at the controls → never gate the loop on a human-facing card.
  useEffect(() => {
    if (tile && active?.isAI) store.dismissNewPropertyAlert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tile?.id, active?.isAI]);

  // 5-second board glow after dismissal, then clean up.
  useEffect(() => {
    if (!store.spotlightTileId) return;
    const timer = window.setTimeout(() => store.clearSpotlight(), 5000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.spotlightTileId]);

  if (!tile) return null;
  const meta = DISTRICT_META[tile.district];
  const total = getDistrictTotal(store.board, tile.district);
  const float = floatSharesOf(tile);

  const row = (label: string, value: string) => (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-slate-400">{label}</span>
      <span className="font-bold text-slate-100">{value}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm">
      <div className="w-full max-w-sm overflow-hidden rounded-2xl border-2 border-amber-400/70 bg-slate-900 shadow-[0_0_40px_rgba(251,191,36,0.35)]">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 text-center">
          <p className="animate-pulse text-sm font-black uppercase tracking-widest text-slate-950">
            {t('expansion.spotlightTitle')}
          </p>
          <p className="text-[11px] font-semibold text-amber-950/80">{t('expansion.spotlightSubtitle')}</p>
        </div>
        <div className="space-y-3 p-5">
          {/* District badge: coloured pill with live tile count against the 5-cap. */}
          <div className="flex items-center justify-between gap-2">
            <span
              className="rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wider text-slate-950"
              style={{ backgroundColor: meta.bar }}
            >
              <DistrictIcon district={tile.district} size={14} /> {meta.label} · {total}/{MAX_DISTRICT_TILES} tiles
            </span>
            <span className="rounded-full border border-violet-400/50 bg-violet-500/15 px-2 py-0.5 text-[10px] font-bold text-violet-200">NEW</span>
          </div>

          <h3 className="text-lg font-black leading-tight text-slate-50">{localizeTile(tile.name, t)}</h3>

          <div className="space-y-1.5 rounded-xl border border-slate-700/60 bg-slate-950/60 p-3">
            {row(t('expansion.initialValuation'), money(tile.valuation > 0 ? tile.valuation : tile.baseCost))}
            {row(t('expansion.initialRentYield'), `${money(tile.rent ?? Math.round((tile.valuation || tile.baseCost) * rentYieldOf(tile)))} (${(rentYieldOf(tile) * 100).toFixed(1)} %)`)}
            {row(t('expansion.publicFloat'), `${float} shares available`)}
          </div>

          <p className="text-[11px] leading-snug text-slate-400">{t('expansion.purchaseRules')}</p>

          <button
            type="button"
            onClick={() => store.dismissNewPropertyAlert()}
            className="w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2.5 text-sm font-extrabold text-slate-950 transition hover:brightness-110"
          >
            {t('expansion.viewOnBoard')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewPropertyAlertModal;
